import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Moon, BookOpen, Sparkles, Save, User, Pencil, Check, X, Trash2, AlertTriangle, RotateCcw, Lock, Eye, EyeOff, Camera, Upload, Menu, LayoutDashboard, MessageSquare, Settings as SettingsIcon, LogOut, Clock, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';
import SEO from '@/components/SEO';

// Use window.location.origin to ensure we always call the correct backend
const API = `${window.location.origin}/api`;
const LOGO_URL = "/images/dea-wordmark.png"; // Small "a.gpt" logo for internal pages

export default function Settings() {
  const { user, getAuthHeader, updateUser, logout } = useAuth();
  const [cycleSyncEnabled, setCycleSyncEnabled] = useState(false);
  const [lastPeriodDate, setLastPeriodDate] = useState(null);
  const [scripturePreference, setScripturePreference] = useState('none');
  const [affirmationPreference, setAffirmationPreference] = useState('none');
  const [weekendPreference, setWeekendPreference] = useState('rest');
  const [saving, setSaving] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Profile editing
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState('');
  
  // Profile image
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);
  
  // Password change
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Sidebar
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [dailyInspiration, setDailyInspiration] = useState(null);
  
  // Trial access state
  const [accessLevel, setAccessLevel] = useState(null);

  // My Glow Up Habits — 5 customizable habit names
  const [habitsMeta, setHabitsMeta] = useState([]);   // [{id,name,pillar,color}, ...]
  const [habitInputs, setHabitInputs] = useState(['', '', '', '', '']);
  const [savingHabits, setSavingHabits] = useState(false);

  useEffect(() => {
    if (user) {
      setCycleSyncEnabled(user.cycle_sync_enabled || false);
      setScripturePreference(user.scripture_preference || 'none');
      setAffirmationPreference(user.affirmation_preference || 'none');
      setWeekendPreference(user.weekend_preference || 'rest');
      setEditedName(user.name || '');
      if (user.last_period_date) {
        setLastPeriodDate(new Date(user.last_period_date));
      }
      
      // Fetch daily inspiration, access level, AND habit settings
      Promise.all([
        axios.get(`${API}/user/daily-inspiration`, { headers: getAuthHeader() }),
        axios.get(`${API}/user/access-level`, { headers: getAuthHeader() }),
        axios.get(`${API}/habits/settings`, { headers: getAuthHeader() }),
      ]).then(([inspirationRes, accessRes, habitsRes]) => {
        setDailyInspiration(inspirationRes.data);
        setAccessLevel(accessRes.data);
        const habits = habitsRes.data?.habits || [];
        setHabitsMeta(habits);
        setHabitInputs(habits.map((h) => h.name || ''));
      }).catch(() => {});
    }
  }, [user, getAuthHeader]);
  
  // Trial access helpers
  const isTrialUser = accessLevel?.type === 'trial';
  const isExpiredTrial = accessLevel?.type === 'expired_trial';
  const trialDaysRemaining = accessLevel?.daysRemaining || 0;
  const upgradePrice = accessLevel?.upgradeAmount || 88;

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword) {
      toast.error('Please fill in all fields');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    setChangingPassword(true);
    try {
      await axios.put(`${API}/user/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      }, { headers: getAuthHeader() });
      toast.success('Password changed successfully!');
      setShowPasswordChange(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleDeleteJourney = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/journey/current`, { headers: getAuthHeader() });
      toast.success('Journey deleted. You can now start fresh!');
      updateUser({ onboarding_complete: false, current_day: 0, current_phase: null });
      setShowDeleteConfirm(false);
    } catch (error) {
      toast.error('Failed to delete journey');
    } finally {
      setDeleting(false);
    }
  };

  const handleSaveName = async () => {
    if (!editedName.trim()) {
      toast.error('Name cannot be empty');
      return;
    }
    
    try {
      await axios.put(`${API}/user/profile`, { name: editedName.trim() }, { headers: getAuthHeader() });
      updateUser({ name: editedName.trim() });
      setIsEditingName(false);
      toast.success('Name updated!');
    } catch (error) {
      toast.error('Failed to update name');
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    // Validate file size (max 1.5MB)
    if (file.size > 1.5 * 1024 * 1024) {
      toast.error('Image must be smaller than 1.5MB');
      return;
    }
    
    setUploadingImage(true);
    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          const base64Image = reader.result;
          await axios.put(`${API}/user/profile-image`, 
            { profile_image: base64Image }, 
            { headers: getAuthHeader() }
          );
          updateUser({ profile_image: base64Image });
          toast.success('Profile picture updated!');
        } catch (error) {
          toast.error('Failed to upload image');
        } finally {
          setUploadingImage(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Failed to process image');
      setUploadingImage(false);
    }
  };

  const handleRemoveImage = async () => {
    setUploadingImage(true);
    try {
      await axios.delete(`${API}/user/profile-image`, { headers: getAuthHeader() });
      updateUser({ profile_image: null });
      toast.success('Profile picture removed');
    } catch (error) {
      toast.error('Failed to remove image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const prefs = {
        cycle_sync_enabled: cycleSyncEnabled,
        last_period_date: lastPeriodDate ? lastPeriodDate.toISOString() : null,
        scripture_preference: scripturePreference,
        affirmation_preference: affirmationPreference,
        weekend_preference: weekendPreference
      };
      
      await axios.put(`${API}/user/preferences`, prefs, { headers: getAuthHeader() });
      updateUser(prefs);
      toast.success('Preferences saved, sis!');
    } catch (error) {
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveHabits = async () => {
    setSavingHabits(true);
    try {
      const payload = {
        habits: habitsMeta.map((h, i) => ({
          id: h.id,
          name: (habitInputs[i] || '').slice(0, 40),
        })),
      };
      const res = await axios.put(`${API}/habits/settings`, payload, { headers: getAuthHeader() });
      const saved = res.data?.habits || [];
      setHabitsMeta(saved);
      setHabitInputs(saved.map((h) => h.name || ''));
      toast.success('Habits saved, sis!');
    } catch (error) {
      toast.error('Failed to save habits');
    } finally {
      setSavingHabits(false);
    }
  };

  return (
    <>
      <SEO 
        title="Settings"
        description="Customize your Dea experience. Manage cycle syncing, preferences, and account settings."
        noindex={true}
      />
      <div className="min-h-screen bg-[#FFFCF7]">
      {/* Sidebar */}
      <AnimatePresence>
        {sidebarOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-[#726158]/20 z-40"
            />
            <motion.aside
              initial={{ x: -280 }}
              animate={{ x: 0 }}
              exit={{ x: -280 }}
              className="fixed left-0 top-0 bottom-0 w-72 bg-[#FFFCF7] border-r border-[#EAD8C2] z-50 flex flex-col"
            >
              <div className="p-4 border-b border-[#EAD8C2] flex items-center justify-between">
                <Link to="/">
                  <img src={LOGO_URL} alt="Dea" className="h-10 w-auto object-contain" />
                </Link>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setSidebarOpen(false)}
                  className="text-[#AA9283] hover:text-[#726158] hover:bg-[#EAD8C2]/50"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>

              <div className="flex-1 p-4 overflow-y-auto">
                {(dailyInspiration?.affirmation || dailyInspiration?.scripture) ? (
                  <div className="space-y-4">
                    {dailyInspiration?.affirmation && (
                      <div className="rounded-xl p-4 bg-gradient-to-br from-[#CAAB84]/10 to-[#EAD8C2]/30 border border-[#CAAB84]/20">
                        <div className="flex items-center gap-2 mb-3">
                          <Sparkles className="h-4 w-4 text-[#CAAB84]" />
                          <span className="text-sm font-medium text-[#726158]">Today's Affirmation</span>
                        </div>
                        <p className="text-sm text-[#726158] italic leading-relaxed">"{dailyInspiration.affirmation}"</p>
                      </div>
                    )}
                    {dailyInspiration?.scripture && (
                      <div className="rounded-xl p-4 bg-gradient-to-br from-[#87A6A7]/10 to-[#EAD8C2]/20 border border-[#87A6A7]/20">
                        <div className="flex items-center gap-2 mb-3">
                          <BookOpen className="h-4 w-4 text-[#87A6A7]" />
                          <span className="text-sm font-medium text-[#726158]">Daily Scripture</span>
                        </div>
                        <p className="text-sm text-[#726158] italic leading-relaxed">
                          "{dailyInspiration.scripture.text || dailyInspiration.scripture}"
                        </p>
                        {dailyInspiration.scripture.verse && (
                          <p className="text-xs text-[#87A6A7] mt-2 font-medium">— {dailyInspiration.scripture.verse}</p>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="rounded-xl p-4 bg-[#EAD8C2]/20 border border-[#EAD8C2] text-center">
                    <Sparkles className="h-6 w-6 text-[#CAAB84] mx-auto mb-2" />
                    <p className="text-sm text-[#AA9283]">Configure your inspiration preferences below</p>
                  </div>
                )}
              </div>

              <div className="p-4 border-t border-[#EAD8C2] space-y-2">
                <Link to="/dashboard" onClick={() => setSidebarOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start text-[#AA9283] hover:text-[#726158] hover:bg-[#EAD8C2]/50">
                    <LayoutDashboard className="h-4 w-4 mr-2" />
                    Dashboard
                  </Button>
                </Link>
                <Link to="/chat" onClick={() => setSidebarOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start text-[#AA9283] hover:text-[#726158] hover:bg-[#EAD8C2]/50">
                    <MessageSquare className="h-4 w-4 mr-2" />
                    Chat with Dea
                  </Button>
                </Link>
                <Link to="/reflections" onClick={() => setSidebarOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start text-[#AA9283] hover:text-[#726158] hover:bg-[#EAD8C2]/50">
                    <BookOpen className="h-4 w-4 mr-2" />
                    Reflections
                  </Button>
                </Link>
                <Link to="/settings" onClick={() => setSidebarOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start text-[#726158] bg-[#EAD8C2]/30">
                    <SettingsIcon className="h-4 w-4 mr-2" />
                    Settings
                  </Button>
                </Link>
                <Button
                  variant="ghost"
                  onClick={logout}
                  className="w-full justify-start text-[#AA9283] hover:text-[#B1897C] hover:bg-[#B1897C]/10"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Logout
                </Button>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Header */}
      <header className="bg-[#FFFCF7]/90 backdrop-blur-lg border-b border-[#EAD8C2] sticky top-0 z-40">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-[#AA9283] hover:text-[#726158]"
            data-testid="sidebar-toggle-btn"
          >
            <Menu className="h-5 w-5" />
          </Button>
          <img src={LOGO_URL} alt="Dea" className="h-10 w-auto object-contain" />
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#AA9283]/10 border border-[#AA9283]/20">
            <SettingsIcon className="h-4 w-4 text-[#AA9283]" />
            <span className="text-sm font-medium text-[#AA9283]">Settings</span>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8 space-y-8">
        <h1 className="font-serif text-2xl text-[#726158]">Settings</h1>

        {/* Trial Banner - subtle indicator for trial users */}
        {(isTrialUser || isExpiredTrial) && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`rounded-xl p-4 flex items-center justify-between gap-4 ${
              isExpiredTrial 
                ? 'bg-[#B1897C]/10 border border-[#B1897C]/30' 
                : 'bg-[#CAAB84]/10 border border-[#CAAB84]/30'
            }`}
            data-testid="settings-trial-banner"
          >
            <div className="flex items-center gap-3">
              {isExpiredTrial ? (
                <>
                  <AlertTriangle className="h-5 w-5 text-[#B1897C]" />
                  <div>
                    <p className="text-sm font-medium text-[#726158]">Your trial has ended</p>
                    <p className="text-xs text-[#AA9283]">Upgrade to continue your journey with Dea</p>
                  </div>
                </>
              ) : (
                <>
                  <Clock className="h-5 w-5 text-[#CAAB84]" />
                  <div>
                    <p className="text-sm font-medium text-[#726158]">7-Day Power Start</p>
                    <p className="text-xs text-[#AA9283]">{trialDaysRemaining} {trialDaysRemaining === 1 ? 'day' : 'days'} remaining</p>
                  </div>
                </>
              )}
            </div>
            <Link to="/checkout?upgrade=true">
              <Button size="sm" className={isExpiredTrial ? 'bg-[#B1897C] hover:bg-[#AA9283] text-white' : 'btn-gold'} data-testid="settings-upgrade-btn">
                <Zap className="h-4 w-4 mr-1" />
                Upgrade — ${upgradePrice}
              </Button>
            </Link>
          </motion.div>
        )}

        {/* Profile */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card rounded-2xl p-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-[#CAAB84]/10 flex items-center justify-center">
              <User className="h-5 w-5 text-[#CAAB84]" />
            </div>
            <h2 className="font-serif text-xl text-[#726158]">Profile</h2>
          </div>
          
          <div className="space-y-6">
            {/* Profile Picture */}
            <div>
              <Label className="text-[#B1897C] text-sm mb-3 block">Profile Picture</Label>
              <div className="flex items-center gap-4">
                <div className="relative">
                  {user?.profile_image ? (
                    <img 
                      src={user.profile_image} 
                      alt="Profile" 
                      className="w-20 h-20 rounded-full object-cover border-2 border-[#EAD8C2]"
                    />
                  ) : (
                    <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#CAAB84] to-[#AA9283] flex items-center justify-center text-white text-2xl font-serif">
                      {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                  )}
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingImage}
                    className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-[#CAAB84] text-white flex items-center justify-center hover:bg-[#AA9283] transition-colors disabled:opacity-50"
                    data-testid="upload-image-btn"
                  >
                    {uploadingImage ? (
                      <span className="spinner w-4 h-4" />
                    ) : (
                      <Camera className="h-4 w-4" />
                    )}
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    data-testid="image-file-input"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingImage}
                    className="text-[#726158] border-[#EAD8C2]"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Upload Photo
                  </Button>
                  {user?.profile_image && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={handleRemoveImage}
                      disabled={uploadingImage}
                      className="text-red-500 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Remove
                    </Button>
                  )}
                </div>
              </div>
              <p className="text-xs text-[#AA9283] mt-2">JPG, PNG or GIF. Max 1.5MB.</p>
            </div>

            {/* Name */}
            <div>
              <Label className="text-[#B1897C] text-sm">Name</Label>
              {isEditingName ? (
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    value={editedName}
                    onChange={(e) => setEditedName(e.target.value)}
                    className="input-light flex-1"
                    autoFocus
                    data-testid="edit-name-input"
                  />
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={handleSaveName}
                    className="text-[#87A6A7] hover:bg-[#87A6A7]/10"
                  >
                    <Check className="h-4 w-4" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => {
                      setEditedName(user?.name || '');
                      setIsEditingName(false);
                    }}
                    className="text-[#B1897C] hover:bg-[#B1897C]/10"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div className="flex items-center justify-between mt-1">
                  <p className="text-[#726158]">{user?.name}</p>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setIsEditingName(true)}
                    className="text-[#CAAB84] hover:bg-[#CAAB84]/10"
                    data-testid="edit-name-btn"
                  >
                    <Pencil className="h-4 w-4 mr-1" />
                    Edit
                  </Button>
                </div>
              )}
            </div>

            {/* Email */}
            <div>
              <Label className="text-[#B1897C] text-sm">Email</Label>
              <p className="text-[#726158] mt-1">{user?.email}</p>
            </div>
          </div>
        </motion.div>

        {/* My Glow Up Habits — customize the 5 daily habits tracked on the Dashboard */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.08 }}
          className="glass-card rounded-2xl p-6"
          data-testid="habit-settings-section"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-[#B8956A]/15 flex items-center justify-center">
              <Sparkles className="h-5 w-5 text-[#B8956A]" />
            </div>
            <div>
              <h2 className="font-serif text-xl text-[#726158]">My Glow Up Habits</h2>
              <p className="text-[#AA9283] text-sm">5 daily habits you'll track from your dashboard</p>
            </div>
          </div>

          <div className="space-y-5">
            {habitsMeta.map((habit, i) => (
              <div key={habit.id} data-testid={`habit-field-${habit.id}`}>
                <div className="flex items-center gap-2 mb-1.5">
                  <span
                    className="inline-block rounded-full"
                    style={{ width: 8, height: 8, backgroundColor: habit.color }}
                  />
                  <span
                    className="text-[11px] uppercase tracking-wide font-semibold"
                    style={{ color: '#B8956A' }}
                  >
                    {habit.pillar}
                  </span>
                </div>
                <Input
                  type="text"
                  maxLength={40}
                  value={habitInputs[i] ?? ''}
                  onChange={(e) => {
                    const next = [...habitInputs];
                    next[i] = e.target.value;
                    setHabitInputs(next);
                  }}
                  placeholder={`e.g. ${habit.name}`}
                  className="border-[#EAD8C2] focus:border-[#B8956A] bg-[#FFFCF7]"
                  data-testid={`habit-input-${habit.id}`}
                />
                <p className="text-[10px] text-[#B1897C] text-right mt-1">
                  {(habitInputs[i] ?? '').length}/40
                </p>
              </div>
            ))}

            {habitsMeta.length === 0 && (
              <p className="text-sm text-[#AA9283] py-4 text-center">Loading your habits…</p>
            )}
          </div>

          <Button
            onClick={handleSaveHabits}
            disabled={savingHabits || habitsMeta.length === 0}
            className="btn-gold w-full mt-5"
            data-testid="save-habits-btn"
          >
            {savingHabits ? <span className="spinner" /> : <><Save className="h-4 w-4 mr-2" /> Save My Habits</>}
          </Button>

          <p className="text-[12px] italic text-[#AA9283] mt-3 text-center leading-relaxed">
            Your habits reset every 30 days so you can build new habits across your 90-day journey.
            Your past data is always saved.
          </p>
        </motion.div>

        {/* Cycle Sync */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="glass-card rounded-2xl p-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-[#B1897C]/10 flex items-center justify-center">
              <Moon className="h-5 w-5 text-[#B1897C]" />
            </div>
            <div>
              <h2 className="font-serif text-xl text-[#726158]">Cycle Syncing</h2>
              <p className="text-[#AA9283] text-sm">Work with your body's natural rhythm, not against it</p>
            </div>
          </div>

          <div className="space-y-6">
            <div className="flex items-center justify-between p-4 bg-[#EAD8C2]/30 rounded-xl">
              <div>
                <Label htmlFor="cycle-sync" className="text-[#726158] font-medium">Enable Cycle Syncing</Label>
                <p className="text-[#AA9283] text-sm mt-1">Dea will tailor your daily tasks to match your energy levels throughout your cycle</p>
              </div>
              <Switch
                id="cycle-sync"
                checked={cycleSyncEnabled}
                onCheckedChange={setCycleSyncEnabled}
                data-testid="cycle-sync-switch"
              />
            </div>

            {!cycleSyncEnabled && (
              <div className="p-4 bg-[#87A6A7]/10 rounded-xl border border-[#87A6A7]/20">
                <p className="text-sm text-[#726158]">
                  <strong>No problem!</strong> Cycle syncing is optional. If you're pregnant, don't have a regular cycle, 
                  or simply prefer not to track, Dea will still give you personalized guidance based on your goals and energy levels.
                </p>
              </div>
            )}

            {cycleSyncEnabled && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="space-y-4"
              >
                <div>
                  <Label className="text-[#AA9283] text-sm mb-2 block">When did your last period start?</Label>
                  <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left bg-[#FFFCF7] border-[#EAD8C2] text-[#726158] hover:bg-[#EAD8C2]/30"
                        data-testid="period-date-btn"
                      >
                        {lastPeriodDate ? format(lastPeriodDate, 'PPP') : 'Select the first day of your last period'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-[#FFFCF7] border-[#EAD8C2]" align="start">
                      <Calendar
                        mode="single"
                        selected={lastPeriodDate}
                        onSelect={(date) => {
                          setLastPeriodDate(date);
                          setCalendarOpen(false);
                        }}
                        disabled={(date) => date > new Date()}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Cycle Phases Explanation - Enhanced */}
                <div className="bg-[#FFFCF7] rounded-xl p-4 border border-[#EAD8C2]">
                  <p className="text-[#726158] font-medium mb-4">Your 28-Day Cycle Phases:</p>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3 p-3 rounded-lg bg-[#B1897C]/10 border border-[#B1897C]/20">
                      <div className="w-4 h-4 rounded-full bg-[#B1897C] mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-[#B1897C]">Menstruation (Days 1-7)</p>
                        <p className="text-xs text-[#AA9283] mt-1">
                          <strong>Energy:</strong> Lowest - time for rest and reflection<br/>
                          <strong>Best for:</strong> Light administrative work, planning, journaling
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-3 rounded-lg bg-[#CAAB84]/10 border border-[#CAAB84]/20">
                      <div className="w-4 h-4 rounded-full bg-[#CAAB84] mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-[#CAAB84]">Follicular (Days 8-14)</p>
                        <p className="text-xs text-[#AA9283] mt-1">
                          <strong>Energy:</strong> Rising - great for starting new projects<br/>
                          <strong>Best for:</strong> Brainstorming, learning, creative work
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-3 rounded-lg bg-[#87A6A7]/10 border border-[#87A6A7]/20">
                      <div className="w-4 h-4 rounded-full bg-[#87A6A7] mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-[#87A6A7]">Ovulation (Days 15-21)</p>
                        <p className="text-xs text-[#AA9283] mt-1">
                          <strong>Energy:</strong> Peak - best for performing and socializing<br/>
                          <strong>Best for:</strong> Presentations, meetings, sales calls, networking
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-3 rounded-lg bg-[#AA9283]/10 border border-[#AA9283]/20">
                      <div className="w-4 h-4 rounded-full bg-[#AA9283] mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-[#AA9283]">Luteal (Days 22-28)</p>
                        <p className="text-xs text-[#AA9283] mt-1">
                          <strong>Energy:</strong> Declining - time to complete and wrap up<br/>
                          <strong>Best for:</strong> Finishing projects, detail work, less social time
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        </motion.div>

        {/* Daily Inspiration */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="glass-card rounded-2xl p-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center">
              <Sparkles className="h-5 w-5 text-[#D4AF37]" />
            </div>
            <div>
              <h2 className="font-serif text-xl text-[#1A1A1A]">Daily Inspiration</h2>
              <p className="text-[#4A4A4A] text-sm">Start each session with intention</p>
            </div>
          </div>

          <div className="space-y-6">
            <div>
              <Label className="text-[#1A1A1A] mb-3 block">Scripture Preference</Label>
              <RadioGroup value={scripturePreference} onValueChange={setScripturePreference}>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="none" id="scripture-none" />
                  <Label htmlFor="scripture-none" className="text-[#4A4A4A]">None</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="daily" id="scripture-daily" />
                  <Label htmlFor="scripture-daily" className="text-[#4A4A4A]">Daily Scripture</Label>
                </div>
              </RadioGroup>
            </div>

            <div>
              <Label className="text-[#1A1A1A] mb-3 block">Affirmation Preference</Label>
              <RadioGroup value={affirmationPreference} onValueChange={setAffirmationPreference}>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="none" id="affirmation-none" />
                  <Label htmlFor="affirmation-none" className="text-[#4A4A4A]">None</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="daily" id="affirmation-daily" />
                  <Label htmlFor="affirmation-daily" className="text-[#4A4A4A]">Daily Affirmation</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="both" id="affirmation-both" />
                  <Label htmlFor="affirmation-both" className="text-[#4A4A4A]">Both Scripture & Affirmation</Label>
                </div>
              </RadioGroup>
            </div>

            <div className="bg-[#FBF9F6] rounded-xl p-4 border border-[#D4AF37]/20">
              <p className="text-[#4A4A4A] text-sm">
                <strong className="text-[#D4AF37]">✨ How it works:</strong> When you start a new chat session, 
                Dea will greet you with an inspirational scripture and/or affirmation based on your selection above.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Weekend Preferences */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="glass-card rounded-2xl p-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-[#8B5CF6]/10 flex items-center justify-center">
              <BookOpen className="h-5 w-5 text-[#8B5CF6]" />
            </div>
            <div>
              <h2 className="font-serif text-xl text-[#1A1A1A]">Weekend Preferences</h2>
              <p className="text-[#4A4A4A] text-sm">How do you want to spend your weekends?</p>
            </div>
          </div>

          <RadioGroup value={weekendPreference} onValueChange={setWeekendPreference}>
            <div className="space-y-3">
              <div className="flex items-center space-x-2 p-3 rounded-lg bg-[#F5F3EF]">
                <RadioGroupItem value="rest" id="weekend-rest" />
                <Label htmlFor="weekend-rest" className="text-[#4A4A4A] flex-1">
                  <span className="block text-[#1A1A1A]">Complete Rest</span>
                  <span className="text-[#8A8A8A] text-sm">No tasks on weekends</span>
                </Label>
              </div>
              <div className="flex items-center space-x-2 p-3 rounded-lg bg-[#F5F3EF]">
                <RadioGroupItem value="light" id="weekend-light" />
                <Label htmlFor="weekend-light" className="text-[#4A4A4A] flex-1">
                  <span className="block text-[#1A1A1A]">Light Tasks</span>
                  <span className="text-[#8A8A8A] text-sm">Smaller, easier tasks on weekends</span>
                </Label>
              </div>
              <div className="flex items-center space-x-2 p-3 rounded-lg bg-[#F5F3EF]">
                <RadioGroupItem value="personal" id="weekend-personal" />
                <Label htmlFor="weekend-personal" className="text-[#4A4A4A] flex-1">
                  <span className="block text-[#1A1A1A]">Personal Growth</span>
                  <span className="text-[#8A8A8A] text-sm">Focus on spiritual, family, or self-care</span>
                </Label>
              </div>
            </div>
          </RadioGroup>
        </motion.div>

        {/* Change Password Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.33 }}
          className="glass-card rounded-2xl p-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-[#87A6A7]/20 flex items-center justify-center">
              <Lock className="h-5 w-5 text-[#87A6A7]" />
            </div>
            <div>
              <h2 className="font-serif text-xl text-[#1A1A1A]">Security</h2>
              <p className="text-[#4A4A4A] text-sm">Update your password</p>
            </div>
          </div>

          {!showPasswordChange ? (
            <div className="flex items-center justify-between p-4 rounded-xl bg-[#EAD8C2]/30">
              <div>
                <p className="font-medium text-[#726158]">Change Password</p>
                <p className="text-sm text-[#AA9283]">Update your account password</p>
              </div>
              <Button
                variant="outline"
                onClick={() => setShowPasswordChange(true)}
                className="border-[#87A6A7] text-[#87A6A7] hover:bg-[#87A6A7]/10"
              >
                <Lock className="h-4 w-4 mr-2" />
                Change
              </Button>
            </div>
          ) : (
            <div className="space-y-4 p-4 rounded-xl bg-[#EAD8C2]/30">
              <div>
                <Label className="text-[#AA9283]">Current Password</Label>
                <Input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="input-light mt-1"
                  placeholder="Enter current password"
                />
              </div>
              <div>
                <Label className="text-[#AA9283]">New Password</Label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="input-light mt-1"
                  placeholder="At least 6 characters"
                />
              </div>
              <div>
                <Label className="text-[#AA9283]">Confirm New Password</Label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="input-light mt-1"
                  placeholder="Confirm new password"
                />
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowPasswordChange(false);
                    setCurrentPassword('');
                    setNewPassword('');
                    setConfirmPassword('');
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleChangePassword}
                  disabled={changingPassword}
                  className="flex-1 bg-[#87A6A7] hover:bg-[#87A6A7]/90 text-white"
                >
                  {changingPassword ? <span className="spinner" /> : 'Update Password'}
                </Button>
              </div>
            </div>
          )}
        </motion.div>

        {/* Danger Zone - Delete Journey */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="glass-card rounded-2xl p-6 border border-red-200"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
              <AlertTriangle className="h-5 w-5 text-red-500" />
            </div>
            <div>
              <h2 className="font-serif text-xl text-[#1A1A1A]">Danger Zone</h2>
              <p className="text-[#4A4A4A] text-sm">Reset your journey and start fresh</p>
            </div>
          </div>

          {!showDeleteConfirm ? (
            <div className="flex items-center justify-between p-4 rounded-xl bg-red-50">
              <div>
                <p className="font-medium text-[#726158]">Delete Current Journey</p>
                <p className="text-sm text-[#AA9283]">This will remove your current 90-day plan and milestones</p>
              </div>
              <Button
                variant="outline"
                onClick={() => setShowDeleteConfirm(true)}
                className="border-red-300 text-red-600 hover:bg-red-100"
                data-testid="delete-journey-btn"
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Start Over
              </Button>
            </div>
          ) : (
            <div className="p-4 rounded-xl bg-red-50 border border-red-200">
              <p className="font-medium text-red-600 mb-2">Are you sure?</p>
              <p className="text-sm text-red-500 mb-4">
                This will permanently delete your current journey, milestones, and progress. 
                You'll be able to start a new journey with Dea.
              </p>
              <div className="flex gap-3">
                <Button
                  onClick={handleDeleteJourney}
                  disabled={deleting}
                  className="bg-red-500 hover:bg-red-600 text-white"
                  data-testid="confirm-delete-btn"
                >
                  {deleting ? <span className="spinner" /> : <><Trash2 className="h-4 w-4 mr-2" /> Yes, Delete Journey</>}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowDeleteConfirm(false)}
                  className="border-gray-300"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </motion.div>

        {/* Save Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="flex flex-col sm:flex-row gap-4"
        >
          <Button
            onClick={handleSave}
            disabled={saving}
            className="btn-gold flex-1"
            data-testid="save-settings-btn"
          >
            {saving ? <span className="spinner" /> : <><Save className="h-4 w-4 mr-2" /> Save Preferences</>}
          </Button>
          <Button
            variant="outline"
            onClick={logout}
            className="border-red-200 text-red-600 hover:bg-red-50"
          >
            Sign Out
          </Button>
        </motion.div>
      </main>
    </div>
    </>
  );
}
